import {
  GridApi,
  GridState,
  gridExpandedSortedRowEntriesSelector,
  gridExpandedSortedRowIdsSelector,
  gridVisibleRowsLookupSelector
} from '@mui/x-data-grid-pro'
import { TFunction } from 'i18next'
import { FilterOption } from './types.d'
import moment from 'moment'
import { apiFormat } from '@cbgms/base/utils/datetime/apiFormat'
import { SortDirection } from '@cbgms/api/helpers'

export function getRowByIndex(apiRef: React.MutableRefObject<GridApi>, index: number) {
  return gridExpandedSortedRowEntriesSelector(apiRef)[index]
}

export function onKeyScroll(apiRef: React.MutableRefObject<GridApi>, index: number) {
  apiRef.current.scrollToIndexes({ rowIndex: index, colIndex: 0 })
  const id = gridExpandedSortedRowIdsSelector(apiRef)[index]
  apiRef.current.setRowSelectionModel([id])
}

export const generateUniqueOptions = (allOptions: FilterOption[]) => {
  const results = [] as FilterOption[]
  const filledOptions = allOptions.filter(option => option.value !== '')
  return filledOptions.reduce((results, option) => {
    if (!results.find(result => result.value === option.value)) {
      results.push(option)
    }
    return results
  }, results)
}

export const sortFilterOptions = (options: FilterOption[], direction = SortDirection.Ascending) => {
  const sortedOptions = options.sort((a: FilterOption, b: FilterOption) => {
    // most values are strings, but we also get numbers in string format.
    // If they cast to a number, compare them in such a way.
    if (typeof a.value === 'string' && typeof b.value === 'string') {
      const isNumericString = !(isNaN(+b.value) && isNaN(+a.value))
      return a.value.localeCompare(b.value, undefined, { numeric: isNumericString })
    }

    // Normal number sorting.
    if (typeof a.value === 'number' && typeof b.value === 'number') {
      return a.value - b.value
    }

    return 0
  })

  return direction === SortDirection.Descending ? sortedOptions.reverse() : sortedOptions
}

export const parseVehicleDate = (date?: string) => {
  const fallbackString = '-'
  const parsed = formatToYearMonth(date, fallbackString)
  if (!parsed || parsed === fallbackString || parsed === '0001-01' || parsed === '0001-01-01') {
    return moment()
  }
  return moment(date, apiFormat.yearMonth)
}

export const buildYearRange = (start: number, end: number) =>
  Array.from({ length: end - start + 1 }, (_, i) => {
    const year = i === 0 ? start : start + 1 * i
    return { value: year, label: year.toString() }
  })

export const getVisibleRowCount = (state: GridState) => {
  return Object.values(gridVisibleRowsLookupSelector(state)).filter(row => !!row).length
}

export function buildYearDisplayValue(t: TFunction, from?: string, until?: string) {
  const fromText = formatToYearMonth(from, '-')
  const untilText = formatToYearMonth(until, t('time.to_date'))
  return `${fromText} ${t('vehicle.tm')} ${untilText}`
}

const formatToYearMonth = (input: string | undefined, missingValueText: string) => {
  if (!input) {
    return missingValueText
  }
  let output = input
  if (input.length >= 6 && input.indexOf('-') === -1) {
    // missing a dash, came in as 'YYYYMM', add a dash and return 'YYYY-MM'
    output = `${input.slice(0, 4)}-${input.slice(4)}`
  }
  if (output === '0001-01') {
    return missingValueText
  }
  return output
}
